import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, ImagePlus, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import ImagePicker from '../components/ImagePicker';
import { LANGS } from '../lib/adminActivities';
import {
  apiEnabled,
  createBlogPost,
  deleteBlogPost,
  fetchAdminBlogPosts,
  toBlogPayload,
  updateBlogPost,
} from '../lib/adminBlog';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const EMPTY_FORM = {
  slug: '', title: {}, excerpt: {}, content: {}, seo_title: {}, seo_description: {},
  image: '', category: '', date: '', read_time: '', author: 'OWA', tags: '',
  published: true, featured: false,
};

function postToForm(p) {
  const i18nOf = (v) => (v && typeof v === 'object' ? { ...v } : v ? { es: v } : {});
  return {
    slug: p.slug || p.id || '',
    title: i18nOf(p.title),
    excerpt: i18nOf(p.excerpt),
    content: i18nOf(p.content),
    seo_title: i18nOf(p.seo_title ?? p.seoTitle),
    seo_description: i18nOf(p.seo_description ?? p.seoDescription),
    image: p.image || '',
    category: p.category || '',
    date: p.date || '',
    read_time: p.read_time || p.readTime || '',
    author: p.author || 'OWA',
    tags: (p.tags || []).join(', '),
    published: p.published !== false,
    featured: Boolean(p.featured),
  };
}

const postTitle = (p) => (p.title && typeof p.title === 'object' ? p.title.es : p.title) || p.slug || p.id;

// Turn a Spanish title into a URL-safe slug.
const slugify = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export default function AdminBlog() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingSlug, setEditingSlug] = useState(null);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [lang, setLang] = useState('es');
  const [picker, setPicker] = useState(false);
  const [visibility, setVisibility] = useState('all');
  const [showSeo, setShowSeo] = useState(false);

  const publishedFilter = visibility === 'all' ? undefined : visibility === 'published';
  const listQuery = useQuery({
    queryKey: ['admin', 'blog', visibility],
    queryFn: () => fetchAdminBlogPosts(publishedFilter),
  });
  const items = listQuery.data?.items || [];

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setI18n = (field, value) =>
    setForm((c) => ({ ...c, [field]: { ...c[field], [lang]: value } }));

  const resetForm = () => { setForm(EMPTY_FORM); setEditingSlug(null); setLang('es'); };
  const startEdit = (p) => {
    setForm(postToForm(p));
    setEditingSlug(p.slug || p.id);
    setLang('es');
    setMsg({ ok: '', err: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'blog'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = toBlogPayload(form);
      return editingSlug ? updateBlogPost(editingSlug, payload) : createBlogPost(payload);
    },
    onSuccess: async () => {
      setMsg({ ok: editingSlug ? 'Entrada actualizada.' : 'Entrada creada.', err: '' });
      resetForm();
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo guardar la entrada.' }),
  });

  const togglePublishMutation = useMutation({
    mutationFn: (p) => updateBlogPost(p.slug, { ...toBlogPayload(postToForm(p)), published: p.published === false }),
    onSuccess: async () => { setMsg({ ok: 'Visibilidad actualizada.', err: '' }); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo cambiar la visibilidad.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug) => deleteBlogPost(slug),
    onSuccess: async () => { setMsg({ ok: 'Entrada eliminada.', err: '' }); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo eliminar.' }),
  });

  const filtered = items.filter((p) =>
    postTitle(p).toLowerCase().includes(search.trim().toLowerCase())
  );

  const submit = (e) => {
    e.preventDefault();
    if (!apiEnabled) { setMsg({ ok: '', err: 'Backend no conectado: define REACT_APP_API_BASE_URL para guardar.' }); return; }
    if (!form.slug.trim() || !(form.title?.es || '').trim()) {
      setMsg({ ok: '', err: 'La URL y el título en español son obligatorios.' });
      setLang('es');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <AdminSection
      eyebrow="Admin"
      title="Blog"
      description="Crea y edita las entradas del blog en los seis idiomas, con imagen de portada y control de publicación."
      widthClassName="max-w-7xl"
      actions={(
        <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {listQuery.data?.source === 'api' ? 'Fuente: API' : 'Fuente: estática'}
        </div>
      )}
    >
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        {/* List */}
        <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#14213d]">{filtered.length} entradas</h2>
              <p className="mt-1 text-sm text-slate-500">Edita, publica u oculta.</p>
            </div>
            <label className="relative block md:w-64">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título"
                className="w-full rounded-full border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#1fa5a3]" />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Todas', count: listQuery.data ? (listQuery.data.publishedCount ?? 0) + (listQuery.data.draftCount ?? 0) : null },
              { id: 'published', label: 'Publicadas', count: listQuery.data?.publishedCount },
              { id: 'draft', label: 'Borradores', count: listQuery.data?.draftCount },
            ].map((tab) => (
              <button key={tab.id} type="button" onClick={() => setVisibility(tab.id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  visibility === tab.id ? 'bg-[#14213d] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                }`}>
                {tab.label}
                {typeof tab.count === 'number' ? (
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${visibility === tab.id ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>{tab.count}</span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="mt-5 divide-y divide-slate-100">
            {filtered.map((p) => {
              const slug = p.slug || p.id;
              const live = p.published !== false;
              return (
                <div key={slug} className={`flex items-center gap-3 py-3 ${live ? '' : 'rounded-xl bg-amber-50/60 px-2'}`}>
                  <img src={p.image} alt="" className={`h-12 w-16 flex-shrink-0 rounded-lg bg-slate-100 object-cover ${live ? '' : 'grayscale'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-[#14213d]">{postTitle(p)}</p>
                      {!live ? (
                        <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                          <EyeOff className="h-3 w-3" />Borrador
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-slate-500">{p.category || 'Sin categoría'} · {p.date || 'sin fecha'}</p>
                  </div>
                  <button type="button" disabled={!apiEnabled} onClick={() => togglePublishMutation.mutate(p)}
                    title={live ? 'Pasar a borrador' : 'Publicar'}
                    className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:opacity-40">
                    {live ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => startEdit(p)} aria-label="Editar"
                    className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" disabled={!apiEnabled || deleteMutation.isPending} aria-label="Eliminar"
                    onClick={() => { if (window.confirm(`¿Eliminar "${postTitle(p)}"? No se puede deshacer.`)) deleteMutation.mutate(slug); }}
                    className="rounded-full border border-slate-200 p-2 text-red-500 transition hover:border-red-300 disabled:opacity-40">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            {!filtered.length && !listQuery.isLoading ? (
              <p className="py-8 text-center text-sm text-slate-500">Sin entradas.</p>
            ) : null}
          </div>
        </section>

        {/* Editor */}
        <section className="min-w-0 h-fit rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#14213d]">{editingSlug ? 'Editar entrada' : 'Nueva entrada'}</h2>
            {editingSlug ? (
              <button type="button" onClick={resetForm} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1fa5a3]">
                <X className="h-4 w-4" />Cancelar
              </button>
            ) : null}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            {/* Language tabs */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                {LANGS.map((l) => {
                  const filled = Boolean((form.title?.[l.id] || '').trim());
                  return (
                    <button key={l.id} type="button" onClick={() => setLang(l.id)} title={l.name}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                        lang === l.id ? 'bg-[#14213d] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                      }`}>
                      {l.label}
                      <span className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-[#1fa5a3]' : lang === l.id ? 'bg-white/40' : 'bg-slate-300'}`} />
                    </button>
                  );
                })}
                <span className="ml-auto text-xs text-slate-500">{LANGS.find((l) => l.id === lang)?.name}</span>
              </div>

              <div className="mt-3 space-y-3">
                <input
                  className={inputCls}
                  placeholder={`Título (${lang.toUpperCase()})${lang === 'es' ? ' · obligatorio' : ''}`}
                  value={form.title?.[lang] || ''}
                  onChange={(e) => {
                    setI18n('title', e.target.value);
                    // Derive the URL from the Spanish title while creating.
                    if (!editingSlug && lang === 'es') set('slug', slugify(e.target.value));
                  }}
                />
                <textarea className={`${inputCls} min-h-20`} placeholder={`Entradilla / resumen (${lang.toUpperCase()})`}
                  value={form.excerpt?.[lang] || ''} onChange={(e) => setI18n('excerpt', e.target.value)} />
                <textarea className={`${inputCls} min-h-64 font-mono text-xs leading-relaxed`}
                  placeholder={`Contenido (${lang.toUpperCase()}) — admite **negrita** y ## títulos`}
                  value={form.content?.[lang] || ''} onChange={(e) => setI18n('content', e.target.value)} />
              </div>

              <button type="button" onClick={() => setShowSeo((s) => !s)}
                className="mt-3 text-xs font-semibold text-[#1fa5a3] hover:underline">
                {showSeo ? 'Ocultar SEO' : 'Editar SEO (título y descripción para Google)'}
              </button>
              {showSeo ? (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Título SEO ({lang.toUpperCase()}) <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({(form.seo_title?.[lang] || '').length}/65)</span>
                    </label>
                    <input className={`${inputCls} mt-1.5`} maxLength={65} placeholder={`Se usa "${(form.title?.[lang] || form.title?.es || '').slice(0, 40) || 'Título'} | OWA Wild Adventure" si lo dejas vacío`}
                      value={form.seo_title?.[lang] || ''} onChange={(e) => setI18n('seo_title', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Meta descripción ({lang.toUpperCase()}) <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({(form.seo_description?.[lang] || '').length}/158)</span>
                    </label>
                    <textarea className={`${inputCls} mt-1.5 min-h-20`} maxLength={158} placeholder="Se usa la entradilla si lo dejas vacío"
                      value={form.seo_description?.[lang] || ''} onChange={(e) => setI18n('seo_description', e.target.value)} />
                  </div>

                  {/* Google-style search result preview */}
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Vista previa en Google</p>
                    <div className="mt-2 max-w-xl">
                      <p className="text-xs text-slate-600">owawild.com › blog › {form.slug || '...'}</p>
                      <p className="mt-0.5 truncate text-lg text-[#1a0dab]">
                        {form.seo_title?.[lang] || `${form.title?.[lang] || form.title?.es || 'Título de la entrada'} | OWA Wild Adventure`}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">
                        {form.seo_description?.[lang] || form.excerpt?.[lang] || form.excerpt?.es || 'Se usará la entradilla como descripción.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <p className="mt-2 text-xs text-slate-500">
                Los idiomas que no rellenes se muestran con el texto en inglés o español.
              </p>
            </div>

            {/* Cover image */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Imagen de portada</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
                  {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="flex flex-1 flex-wrap gap-2">
                  <button type="button" onClick={() => setPicker(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                    <ImagePlus className="h-3.5 w-3.5" />Elegir o subir
                  </button>
                  {form.image ? (
                    <button type="button" onClick={() => set('image', '')}
                      className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 transition hover:border-red-300 hover:text-red-500">
                      Quitar
                    </button>
                  ) : null}
                </div>
              </div>
              <input className={`${inputCls} mt-3`} type="url" placeholder="…o pega una URL"
                value={form.image} onChange={(e) => set('image', e.target.value)} />
            </div>

            <input className={inputCls} placeholder="URL de la entrada (ej. guia-isla-lobos)" value={form.slug}
              onChange={(e) => set('slug', e.target.value)} disabled={Boolean(editingSlug)} required />
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inputCls} placeholder="Categoría (ej. GUÍAS)" value={form.category} onChange={(e) => set('category', e.target.value)} />
              <input className={inputCls} placeholder="Fecha (ej. 15 Jul 2025)" value={form.date} onChange={(e) => set('date', e.target.value)} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inputCls} placeholder="Autor" value={form.author} onChange={(e) => set('author', e.target.value)} />
              <input className={inputCls} placeholder="Tiempo de lectura (ej. 6 min)" value={form.read_time} onChange={(e) => set('read_time', e.target.value)} />
            </div>
            <input className={inputCls} placeholder="Etiquetas separadas por comas" value={form.tags} onChange={(e) => set('tags', e.target.value)} />

            <div className="flex flex-wrap gap-5 pt-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)} />
                Publicada en la web
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} />
                Destacada
              </label>
            </div>

            <button type="submit" disabled={saveMutation.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
              <Plus className="h-4 w-4" />
              {saveMutation.isPending ? 'Guardando…' : editingSlug ? 'Guardar cambios' : 'Crear entrada'}
            </button>
          </form>
        </section>
      </div>

      <ImagePicker
        open={picker}
        title="Imagen de portada"
        onClose={() => setPicker(false)}
        onSelect={(url) => { set('image', url); setPicker(false); }}
      />
    </AdminSection>
  );
}
